
import SlotMap from '@neumatter/slot-map'
import { Locale, DateTimeFormat } from '@neumatter/intl'
import PlainTime from './PlainTime.js'
import PlainDate from './PlainDate.js'
import Duration from './Duration.js'
import Calendar from './Calendar.js'
import TimeZone from './TimeZone.js'
import { toSecondsStringPrecision, formatSecondsStringPart, getOption } from './helpers/utils.js'
import { rejectDateTime } from './helpers/regulate.js'
import { parseISODateTime } from './helpers/parsers.js'
import { MILLION, THOUSAND, ZERO, BILLION } from './helpers/constants.js'
import { differenceZonedDateTime, roundNanoseconds } from './helpers/math.js'

export function yearToISOString (year) {
  const sign = year < 0 ? '-' : '+'

  return sign === '-' || year > 9999
    ? sign + `000000${Math.abs(year)}`.slice(-6)
    : `0000${year}`.slice(-4)
}

const SYMBOL_IS_DATE = Symbol.for('neumatter.DateTime')

const timestamp = (() => {
  if (
    typeof globalThis.performance === 'undefined' ||
    typeof globalThis.performance.timeOrigin !== 'number' ||
    typeof globalThis.performance.now !== 'function'
  ) {
    return () => {
      return BigInt(Date.now()) * MILLION
    }
  }

  const start = globalThis.performance.timeOrigin

  return () => {
    const milliseconds = start + globalThis.performance.now()
    const epochMilliseconds = Math.trunc(milliseconds)
    return (
      BigInt(epochMilliseconds) * MILLION +
      BigInt(Math.trunc((milliseconds - epochMilliseconds) * 1000000))
    )
  }
})()

export default class DateTime {
  static now (options = { timeZone: 'UTC', calendar: 'iso8601' }) {
    return DateTime.from(timestamp(), options)
  }

  static isDateTime (item) {
    return (
      typeof item === 'object' &&
      item !== null &&
      typeof item[SYMBOL_IS_DATE] === 'boolean'
    )
  }

  static compare (a, b) {
    a = DateTime.isDateTime(a) ? a.epochNanoseconds : DateTime.from(a).epochNanoseconds
    b = DateTime.isDateTime(b) ? b.epochNanoseconds : DateTime.from(b).epochNanoseconds
    return a > b ? 1 : a < b ? -1 : 0
  }

  static max (...dateTimes) {
    let index = 0
    let max = DateTime.isDateTime(dateTimes[index])
      ? dateTimes[index]
      : DateTime.from(dateTimes[index])

    const length = dateTimes.length

    while (++index < length) {
      let dateTime = dateTimes[index]
      dateTime = DateTime.isDateTime(dateTime)
        ? dateTime
        : DateTime.from(dateTime)

      if (dateTime.epochNanoseconds > max.epochNanoseconds) {
        max = dateTime
      }
    }

    return max
  }

  static min (...dateTimes) {
    let index = 0
    let min = DateTime.isDateTime(dateTimes[index])
      ? dateTimes[index]
      : DateTime.from(dateTimes[index])

    const length = dateTimes.length

    while (++index < length) {
      let dateTime = dateTimes[index]
      dateTime = DateTime.isDateTime(dateTime)
        ? dateTime
        : DateTime.from(dateTime)

      if (dateTime.epochNanoseconds < min.epochNanoseconds) {
        min = dateTime
      }
    }

    return min
  }

  /**
   *
   * @param {any} item
   * @param {{ timeZone: string, calendar: string, overflow: string }} options
   * @returns {DateTime}
   */
  static from (item, options = { timeZone: 'UTC', calendar: 'iso8601', overflow: 'constrain' }) {
    if (typeof options !== 'object') {
      options = { timeZone: 'UTC', calendar: 'iso8601', overflow: 'constrain' }
    }

    switch (typeof item) {
      case 'undefined':
        return DateTime.now(options)
      case 'string': {
        const data = parseISODateTime(item)
        const overflow = typeof options.overflow === 'string' ? options.overflow : 'constrain'

        if (data.second > 59) {
          if (overflow === 'constrain') {
            data.second = 59
          } else if (overflow === 'reject') {
            throw new RangeError(
              '"' + String(data.second) +
              '" second is out of range. Must be in range of 0-59.'
            )
          }
        }

        return new DateTime(
          data.year,
          data.month,
          data.day,
          data.hour,
          data.minute,
          data.second,
          data.millisecond,
          data.microsecond,
          data.nanosecond,
          data.ianaName,
          data.calendar
        )
      }
      case 'object': {
        if (item === null) {
          return DateTime.now(options)
        } else if (DateTime.isDateTime(item)) {
          item = item.getISOFields()
          return new DateTime(
            item.year,
            item.month,
            item.day,
            item.hour,
            item.minute,
            item.second,
            item.millisecond,
            item.microsecond,
            item.nanosecond,
            item.timeZone,
            item.calendar
          )
        } else if (
          'year' in item ||
          'month' in item ||
          'monthCode' in item ||
          'week' in item ||
          'day' in item ||
          'hour' in item ||
          'minute' in item ||
          'second' in item ||
          'millisecond' in item ||
          'microsecond' in item ||
          'nanosecond' in item
        ) {
          const calendar = Calendar.from(item.calendar ?? options.calendar)

          const fields = {
            year: item.year ?? 1970,
            day: item.day ?? 1
          }

          if ('month' in item) {
            fields.month = item.month
          } else if ('monthCode' in item) {
            fields.monthCode = item.monthCode
          } else {
            fields.month = 1
          }

          const plainDate = calendar.dateFromFields(fields, options)
          const plainDateSlots = SlotMap.get(plainDate)

          return new DateTime(
            plainDateSlots.get('year'),
            plainDateSlots.get('month'),
            plainDateSlots.get('day'),
            item.hour ?? 0,
            item.minute ?? 0,
            item.second ?? 0,
            item.millisecond ?? 0,
            item.microsecond ?? 0,
            item.nanosecond ?? 0,
            item.timeZone ?? options.timeZone,
            calendar
          )
        } else if ('timeZone' in item || 'calendar' in item) {
          options = item
          return DateTime.now(options)
        } else if (item instanceof Date) {
          const nanoseconds = BigInt(item.valueOf()) * MILLION
          const timeZone = TimeZone.from(options.timeZone)
          const initialDate = timeZone.fieldsFromEpoch(nanoseconds)

          return new DateTime(
            initialDate.year,
            initialDate.month,
            initialDate.day,
            initialDate.hour,
            initialDate.minute,
            initialDate.second,
            initialDate.millisecond,
            initialDate.microsecond,
            initialDate.nanosecond,
            timeZone,
            options.calendar
          )
        } else {
          throw new Error('item failed to be parsed')
        }
      }

      // Assume number is milliseconds from epoch
      case 'number':
        item = BigInt(item) * MILLION
      /* eslint-disable no-fallthrough */
      case 'bigint': {
        const timeZone = TimeZone.from(options.timeZone)
        const initialDate = timeZone.fieldsFromEpoch(item)

        return new DateTime(
          initialDate.year,
          initialDate.month,
          initialDate.day,
          initialDate.hour,
          initialDate.minute,
          initialDate.second,
          initialDate.millisecond,
          initialDate.microsecond,
          initialDate.nanosecond,
          timeZone,
          options.calendar
        )
      }
      default:
        throw new Error('could not parse item')
    }
  }

  constructor (
    year,
    month,
    day,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond,
    timeZone,
    calendar
  ) {
    if (year === undefined) {
      ;({
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond,
        microsecond,
        nanosecond
      } = partsFromEpoch(timestamp()))
    }

    const slotMap = SlotMap.get(this)

    if (typeof year === 'number') {
      slotMap.set('year', year)
      slotMap.set('month', month ?? 1)
      slotMap.set('day', day ?? 1)
      slotMap.set('hour', hour ?? 0)
      slotMap.set('minute', minute ?? 0)
      slotMap.set('second', second ?? 0)
      slotMap.set('millisecond', millisecond ?? 0)
      slotMap.set('microsecond', microsecond ?? 0)
      slotMap.set('nanosecond', nanosecond ?? 0)
      slotMap.set('timeZone', TimeZone.from(timeZone))
      slotMap.set('calendar', Calendar.from(calendar))
    }

    rejectDateTime(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      slotMap.get('hour'),
      slotMap.get('minute'),
      slotMap.get('second'),
      slotMap.get('millisecond'),
      slotMap.get('microsecond'),
      slotMap.get('nanosecond')
    )
  }

  get [SYMBOL_IS_DATE] () {
    return true
  }

  get isZonedDate () {
    const timeZone = SlotMap.getInternal(this, 'timeZone')
    return timeZone.id !== 'UTC'
  }

  get hasISOCalendar () {
    return this.calendar.id === 'iso8601'
  }

  /**
   *
   * @type {Calendar}
   */
  get calendar () {
    return SlotMap.getInternal(this, 'calendar')
  }

  get year () {
    return this.calendar.year(this)
  }

  get month () {
    return this.calendar.month(this)
  }

  get monthCode () {
    return this.calendar.monthCode(this)
  }

  get day () {
    return this.calendar.day(this)
  }

  get hour () {
    return SlotMap.getInternal(this, 'hour')
  }

  get minute () {
    return SlotMap.getInternal(this, 'minute')
  }

  get second () {
    return SlotMap.getInternal(this, 'second')
  }

  get millisecond () {
    return SlotMap.getInternal(this, 'millisecond')
  }

  get microsecond () {
    return SlotMap.getInternal(this, 'microsecond')
  }

  get nanosecond () {
    return SlotMap.getInternal(this, 'nanosecond')
  }

  get timeZone () {
    return SlotMap.getInternal(this, 'timeZone')
  }

  get era () {
    return this.calendar.era(this)
  }

  get eraYear () {
    return this.calendar.eraYear(this)
  }

  get dayOfWeek () {
    return this.calendar.dayOfWeek(this)
  }

  get dayOfYear () {
    return this.calendar.dayOfYear(this)
  }

  get weekOfYear () {
    return this.calendar.weekOfYear(this)
  }

  get daysInWeek () {
    return this.calendar.daysInWeek(this)
  }

  get daysInYear () {
    return this.calendar.daysInYear(this)
  }

  get daysInMonth () {
    return this.calendar.daysInMonth(this)
  }

  get monthsInYear () {
    return this.calendar.monthsInYear(this)
  }

  get inLeapYear () {
    return this.calendar.inLeapYear(this)
  }

  get epochSeconds () {
    return Number(this.epochNanoseconds / BILLION)
  }

  get epochMilliseconds () {
    return Number(this.epochNanoseconds / MILLION)
  }

  get epochNanoseconds () {
    return this.timeZone.getEpochNanoseconds(this)
  }

  get offsetNanoseconds () {
    return this.timeZone.getOffsetNanosecondsFor(this)
  }

  get offset () {
    return this.timeZone.getOffsetStringFor(this)
  }

  get [Symbol.toStringTag] () {
    return 'DateTime'
  }

  get startOfDay () {
    const slotMap = SlotMap.get(this)

    return new DateTime(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      0,
      0,
      0,
      0,
      0,
      0,
      this.timeZone,
      this.calendar
    )
  }

  getISOFields () {
    const slotMap = SlotMap.get(this)

    return {
      year: slotMap.get('year'),
      month: slotMap.get('month'),
      day: slotMap.get('day'),
      hour: slotMap.get('hour'),
      minute: slotMap.get('minute'),
      second: slotMap.get('second'),
      millisecond: slotMap.get('millisecond'),
      microsecond: slotMap.get('microsecond'),
      nanosecond: slotMap.get('nanosecond'),
      timeZone: this.timeZone,
      calendar: this.calendar
    }
  }

  /**
   *
   * @param {import('../types/index').ISODateLike} isoDateLike
   * @returns {DateTime}
   */
  with (isoDateLike) {
    let {
      year,
      month,
      day,
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      timeZone,
      calendar
    } = this.getISOFields()

    if (typeof isoDateLike.year === 'number') {
      year = isoDateLike.year
    }

    if (typeof isoDateLike.month === 'number') {
      month = isoDateLike.month
    }

    if (typeof isoDateLike.day === 'number') {
      day = isoDateLike.day
    }

    if (typeof isoDateLike.hour === 'number') {
      hour = isoDateLike.hour
    }

    if (typeof isoDateLike.minute === 'number') {
      minute = isoDateLike.minute
    }

    if (typeof isoDateLike.second === 'number') {
      second = isoDateLike.second
    }

    if (typeof isoDateLike.millisecond === 'number') {
      millisecond = isoDateLike.millisecond
    }

    if (typeof isoDateLike.microsecond === 'number') {
      microsecond = isoDateLike.microsecond
    }

    if (typeof isoDateLike.nanosecond === 'number') {
      nanosecond = isoDateLike.nanosecond
    }

    if ('timeZone' in isoDateLike) {
      timeZone = isoDateLike.timeZone
    }

    if ('calendar' in isoDateLike) {
      calendar = isoDateLike.calendar
    }

    return new DateTime(
      year,
      month,
      day,
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      timeZone,
      calendar
    )
  }

  withTimeZone (timeZone = 'UTC') {
    return DateTime.from(this.epochNanoseconds, { timeZone, calendar: this.calendar })
  }

  withCalendar (calendar = 'iso8601') {
    return DateTime.from(this.epochNanoseconds, { timeZone: this.timeZone, calendar })
  }

  add (durationLike) {
    const slotMap = SlotMap.get(this)
    const duration = Duration.from(durationLike)

    if (duration.years === 0 && duration.months === 0 && duration.weeks === 0 && duration.days === 0) {
      let sum = ZERO
      sum = sum + BigInt(duration.nanoseconds)
      sum = sum + BigInt(duration.microseconds) * THOUSAND
      sum = sum + BigInt(duration.milliseconds) * MILLION
      sum = sum + BigInt(duration.seconds) * BILLION
      sum = sum + BigInt(duration.minutes) * BigInt(60 * 1e9)
      sum = sum + BigInt(duration.hours) * BigInt(60 * 60 * 1e9)

      return DateTime.from(this.epochNanoseconds + sum, { timeZone: this.timeZone, calendar: this.calendar })
    }

    // Delegate the date part addition to the calendar
    const datePart = new DateTime(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      0,
      0,
      0,
      0,
      0,
      0,
      null,
      this.calendar
    )

    const dateDuration = new Duration(
      duration.years,
      duration.months,
      duration.weeks,
      duration.days,
      0,
      0,
      0,
      0,
      0,
      0
    )

    const addedDate = this.calendar.dateAdd(datePart, dateDuration)
    const addedDateSlots = SlotMap.get(addedDate)

    const intermediateDate = new DateTime(
      addedDateSlots.get('year'),
      addedDateSlots.get('month'),
      addedDateSlots.get('day'),
      this.hour,
      this.minute,
      this.second,
      this.millisecond,
      this.microsecond,
      this.nanosecond,
      this.timeZone,
      this.calendar
    )

    let sum = ZERO
    sum = sum + BigInt(duration.nanoseconds)
    sum = sum + BigInt(duration.microseconds) * THOUSAND
    sum = sum + BigInt(duration.milliseconds) * MILLION
    sum = sum + BigInt(duration.seconds) * BILLION
    sum = sum + BigInt(duration.minutes) * BigInt(60 * 1e9)
    sum = sum + BigInt(duration.hours) * BigInt(60 * 60 * 1e9)

    return DateTime.from(
      intermediateDate.epochNanoseconds + sum,
      { timeZone: this.timeZone, calendar: this.calendar }
    )
  }

  subtract (durationLike) {
    const slotMap = SlotMap.get(this)
    const duration = Duration.from(durationLike).negate()

    if (duration.years === 0 && duration.months === 0 && duration.weeks === 0 && duration.days === 0) {
      let sum = ZERO
      sum = sum + BigInt(duration.nanoseconds)
      sum = sum + BigInt(duration.microseconds) * THOUSAND
      sum = sum + BigInt(duration.milliseconds) * MILLION
      sum = sum + BigInt(duration.seconds) * BILLION
      sum = sum + BigInt(duration.minutes) * BigInt(60 * 1e9)
      sum = sum + BigInt(duration.hours) * BigInt(60 * 60 * 1e9)

      return DateTime.from(this.epochNanoseconds + sum, { timeZone: this.timeZone, calendar: this.calendar })
    }

    // Delegate the date part addition to the calendar
    const datePart = new DateTime(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      0,
      0,
      0,
      0,
      0,
      0,
      null,
      this.calendar
    )

    const dateDuration = new Duration(
      duration.years,
      duration.months,
      duration.weeks,
      duration.days,
      0,
      0,
      0,
      0,
      0,
      0
    )

    const addedDate = this.calendar.dateAdd(datePart, dateDuration)
    const addedDateSlots = SlotMap.get(addedDate)

    const intermediateDate = new DateTime(
      addedDateSlots.get('year'),
      addedDateSlots.get('month'),
      addedDateSlots.get('day'),
      this.hour,
      this.minute,
      this.second,
      this.millisecond,
      this.microsecond,
      this.nanosecond,
      this.timeZone,
      this.calendar
    )

    let sum = ZERO
    sum = sum + BigInt(duration.nanoseconds)
    sum = sum + BigInt(duration.microseconds) * THOUSAND
    sum = sum + BigInt(duration.milliseconds) * MILLION
    sum = sum + BigInt(duration.seconds) * BILLION
    sum = sum + BigInt(duration.minutes) * BigInt(60 * 1e9)
    sum = sum + BigInt(duration.hours) * BigInt(60 * 60 * 1e9)

    return DateTime.from(
      intermediateDate.epochNanoseconds + sum,
      { timeZone: this.timeZone, calendar: this.calendar }
    )
  }

  until (date, options = { largestUnit: 'hour' }) {
    return differenceZonedDateTime('until', this, date, options)
  }

  since (date, options = { largestUnit: 'hour' }) {
    return differenceZonedDateTime('since', this, date, options)
  }

  inspect () {
    return `DateTime(${this.toString()})`
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `DateTime(\x1b[35m${this.toString()}\x1b[0m)`
  }

  toUTCDate () {
    return DateTime.from(this.epochNanoseconds, {
      timeZone: 'UTC'
    })
  }

  toPlainTime () {
    return new PlainTime(
      this.hour,
      this.minute,
      this.second,
      this.millisecond,
      this.microsecond,
      this.nanosecond,
      this.calendar
    )
  }

  toPlainDate () {
    const { year, month, day, calendar } = this.toISOFields()

    return new PlainDate(
      year,
      month,
      day,
      calendar
    )
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }

  toLocaleString (locales = Locale.systemLocale, options = {}) {
    if (!('timeZone' in options) && this.isZonedDate) {
      options.timeZone = this.timeZone.id
    }

    if (!('calendar' in options) && this.calendar.id !== 'iso8601') {
      options.calendar = this.calendar.id
    }

    const df = new DateTimeFormat(locales, options)
    return df.format(this)
  }

  toLocaleDateString (locales = Locale.systemLocale, options = {}) {
    if (!('timeZone' in options) && this.isZonedDate) {
      options.timeZone = this.timeZone.id
    }

    if (!('calendar' in options) && this.calendar.id !== 'iso8601') {
      options.calendar = this.calendar.id
    }

    const opts = {}

    if ('timeZone' in options) {
      opts.timeZone = options.timeZone
    }

    if ('calendar' in options) {
      opts.calendar = options.calendar
    }

    let setDateStyle = true

    if ('dateStyle' in options) {
      opts.dateStyle = options.dateStyle
      setDateStyle = false
    }

    if ('year' in options) {
      opts.year = options.year
      setDateStyle = false
    }

    if ('month' in options) {
      opts.month = options.month
      setDateStyle = false
    }

    if ('day' in options) {
      opts.day = options.day
      setDateStyle = false
    }

    if ('weekday' in options) {
      opts.weekday = options.weekday
      setDateStyle = false
    }

    if ('dayPeriod' in options) {
      opts.dayPeriod = options.dayPeriod
      setDateStyle = false
    }

    if ('era' in options) {
      opts.era = options.era
      setDateStyle = false
    }

    if (setDateStyle) {
      opts.dateStyle = 'short'
    }

    const df = new DateTimeFormat(locales, opts)
    return df.format(this)
  }

  toString (options) {
    let instant = this.epochNanoseconds
    let slotMap = SlotMap.get(this)
    let isoDate = this
    const opts = Object.create(options === undefined ? {} : options)
    const { precision, unit, increment } = toSecondsStringPrecision(opts)

    if ('fractionalDigits' in opts || 'smallestUnit' in opts) {
      const roundingMode = getOption(
        opts,
        'roundingMode',
        'string',
        ['ceil', 'floor', 'expand', 'trunc', 'halfCeil', 'halfFloor', 'halfExpand', 'halfTrunc', 'halfEven'],
        'trunc'
      )

      instant = roundNanoseconds(this.epochNanoseconds, increment, unit, roundingMode)
      isoDate = DateTime.from(instant, { timeZone: this.timeZone, calendar: this.calendar })
      slotMap = SlotMap.get(isoDate)
    }

    const year = yearToISOString(slotMap.get('year'))
    const month = `00${slotMap.get('month')}`.slice(-2)
    const day = `00${slotMap.get('day')}`.slice(-2)
    const hour = `00${slotMap.get('hour')}`.slice(-2)
    const minute = `00${slotMap.get('minute')}`.slice(-2)
    const seconds = formatSecondsStringPart(
      slotMap.get('second'),
      slotMap.get('millisecond'),
      slotMap.get('microsecond'),
      slotMap.get('nanosecond'),
      precision
    )

    let result = `${year}-${month}-${day}T${hour}:${minute}${seconds}`

    const timeZoneName = getOption(
      opts,
      'timeZoneName',
      'string',
      ['auto', 'never'],
      'auto'
    )

    if (this.isZonedDate) {
      result += this.offset

      if (timeZoneName !== 'never') {
        result += `[${this.timeZone}]`
      }
    } else {
      result += 'Z'
    }

    const calendarName = getOption(
      opts,
      'timeZoneName',
      'string',
      ['auto', 'always', 'never'],
      'auto'
    )

    if (
      calendarName === 'always' || (
        calendarName === 'auto' && this.calendar.id !== 'iso8601'
      )
    ) {
      result += `[u-ca=${this.calendar.id}]`
    }

    return result
  }

  valueOf () {
    return this.epochNanoseconds
  }
}

/**
 *
 * @param {bigint} epochNanoseconds
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number, millisecond: number, microsecond: number, nanosecond: number, epochNanoseconds: number, epochMilliseconds: number }}
 */
export function partsFromEpoch (epochNanoseconds) {
  const quotient = epochNanoseconds / MILLION
  const remainder = epochNanoseconds % MILLION
  let epochMilliseconds = Number(quotient)
  let nanos = Number(remainder)

  if (nanos < 0) {
    nanos += 1e6
    epochMilliseconds -= 1
  }

  const microsecond = Math.floor(nanos / 1e3) % 1e3
  const nanosecond = nanos % 1e3

  const legacyDate = new Date(epochMilliseconds)
  const year = legacyDate.getUTCFullYear()
  const month = legacyDate.getUTCMonth() + 1
  const day = legacyDate.getUTCDate()
  const hour = legacyDate.getUTCHours()
  const minute = legacyDate.getUTCMinutes()
  const second = legacyDate.getUTCSeconds()
  const millisecond = legacyDate.getUTCMilliseconds()

  return { year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, epochNanoseconds, epochMilliseconds }
}
